import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";

import flagIL from "@/assets/flags/il.svg";
import flagUS from "@/assets/flags/us.svg";
import flagES from "@/assets/flags/es.svg";

export type LanguageCode = "he" | "en" | "es";

interface Language {
  code: LanguageCode;
  label: string;
  /** Emoji fallback */
  flag: string;
  /** Preferred, consistent icon across devices */
  flagSrc?: string;
  flagEmoji: string;
  currency: string;
  currencyCode: string;
  dir: "rtl" | "ltr";
}

export const languages: Language[] = [
  { code: "he", label: "עברית", flag: "🇮🇱", flagSrc: flagIL, flagEmoji: "🇮🇱", currency: "₪", currencyCode: "ILS", dir: "rtl" },
  { code: "en", label: "English", flag: "🇺🇸", flagSrc: flagUS, flagEmoji: "🇺🇸", currency: "$", currencyCode: "USD", dir: "ltr" },
  { code: "es", label: "Español", flag: "🇪🇸", flagSrc: flagES, flagEmoji: "🇪🇸", currency: "$", currencyCode: "USD", dir: "ltr" },
];

interface LocaleContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  formatPrice: (priceILS: number) => string;
  currency: string;
  dir: "rtl" | "ltr";
  isRTL: boolean;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

// Approximate conversion rate (in real app, would fetch from API)
const ILS_TO_USD_RATE = 0.27;

export const LocaleProvider = ({ children }: { children: ReactNode }) => {
  const { i18n } = useTranslation();
  const [language, setLanguageState] = useState<Language>(languages[0]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    i18n.changeLanguage(lang.code);
    
    // Update document direction
    document.documentElement.dir = lang.dir;
    document.documentElement.lang = lang.code;
    
    // Save to localStorage
    localStorage.setItem("preferred-language", lang.code);
  };

  // Initialize language from localStorage on mount
  useEffect(() => {
    const savedLang = localStorage.getItem("preferred-language") as LanguageCode | null;
    if (savedLang) {
      const found = languages.find(l => l.code === savedLang);
      if (found) {
        setLanguage(found);
      }
    }
  }, []);

  const formatPrice = (priceILS: number): string => {
    if (language.code === "he") {
      return `₪${priceILS.toLocaleString("he-IL")}`;
    }
    // Convert to USD for non-Hebrew languages
    const priceUSD = Math.round(priceILS * ILS_TO_USD_RATE);
    return `$${priceUSD.toLocaleString("en-US")}`;
  };

  const value: LocaleContextType = {
    language,
    setLanguage,
    formatPrice,
    currency: language.currency,
    dir: language.dir,
    isRTL: language.dir === "rtl",
  };

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = (): LocaleContextType => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within a LocaleProvider");
  }
  return context;
};
